import type { Socket } from "socket.io-client";
import { EVENTS } from "@bash/shared";
import type { UserFileReadyPayload } from "@bash/shared";
import { useFileUrlStore } from "../fileUrlStore";
import { useTaskStore } from "../taskStore";
import { toCanonicalPath } from "@/lib/file-paths";
import type { ContentBlock } from "../types";

const IMAGE_MEDIA_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export function registerFilesHandlers(socket: Socket): () => void {
  const handleFileReady = (data: UserFileReadyPayload) => {
    // Canonicalise the fileUrlStore key. Server emits absolute sandbox
    // paths (e.g. `/app/workspace/files/foo.png`) but readers in
    // ToolUseBlock.tsx look up using the agent's relative `files/foo.png`.
    // Without this wrap, a user-files watcher event firing before
    // TASK_OUTPUT_REGISTERED leaves the entry invisible to lookups.
    useFileUrlStore.getState().set(toCanonicalPath(data.filePath), {
      url: data.cdnUrl || undefined,
      base64: data.base64,
      mediaType: data.mediaType,
    });

    // Inject inline image into the task's latest assistant message
    const imageUrl = data.cdnUrl || undefined;
    const isImage = IMAGE_MEDIA_TYPES.has(data.mediaType);

    if (isImage && (imageUrl || data.base64)) {
      // Build image block — prefer CDN URL over base64
      const imageBlock: ContentBlock = imageUrl
        ? {
            type: "image",
            data: "",
            url: imageUrl,
            mediaType: data.mediaType as
              | "image/png"
              | "image/jpeg"
              | "image/gif"
              | "image/webp",
          }
        : {
            type: "image",
            data: data.base64!,
            mediaType: data.mediaType as
              | "image/png"
              | "image/jpeg"
              | "image/gif"
              | "image/webp",
          };

      useTaskStore.getState()._mutateTasks((tasks) => {
        const task = tasks.get(data.taskId);
        if (!task) return;

        for (let i = task.messages.length - 1; i >= 0; i--) {
          if (task.messages[i].role === "assistant") {
            task.messages[i].content.push(imageBlock);
            break;
          }
        }
      });
    }
  };

  socket.on(EVENTS.USER_FILE_READY, handleFileReady);

  return () => {
    socket.off(EVENTS.USER_FILE_READY, handleFileReady);
  };
}
